import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import {
  MdDashboard, MdAddBox, MdList, MdLocationOn,
  MdAssignment, MdDescription, MdStorage,
  MdPeople, MdBarChart, MdLogout,
  MdRssFeed, MdSwapHoriz, MdRouter, MdClose, MdSettings,
  MdInsights, MdHistory, MdMenuBook, MdBusiness, MdMonitorHeart,
  MdSystemUpdate, MdViewList,
} from 'react-icons/md';

// Sidebar navigation. Note: Handheld Simulator, Handheld Guide, and Asset
// Search were intentionally removed from the sidebar at the user's request.
// The underlying routes & backend APIs are kept intact (still reachable by
// direct URL, used by the Android client and other entry points) — only the
// sidebar entries are gone.
//
// Analytics deliberately sits below Reports (per user request); User Guide
// sits at the very bottom right above Settings as a help shortcut.
const navItems = [
  { path: '/dashboard',          label: 'Dashboard',         icon: <MdDashboard />,  perm: 'dashboard' },
  { path: '/asset-registration', label: 'Asset Registration',icon: <MdAddBox />,     perm: 'asset-registration' },
  { path: '/all-assets',         label: 'All Assets',        icon: <MdList />,       perm: 'all-assets' },
  { path: '/location-master',    label: 'Location Master',   icon: <MdLocationOn />, perm: 'location-master' },
  { path: '/asset-movement',     label: 'Asset Movement',    icon: <MdSwapHoriz />,  perm: 'asset-movement' },
  { path: '/asset-audit',        label: 'Asset Audit',       icon: <MdAssignment />, perm: 'asset-audit' },
  { path: '/audit-report',       label: 'Audit Report',      icon: <MdDescription />,perm: 'audit-report' },
  { path: '/raw-inventory',      label: 'Raw Inventory',     icon: <MdStorage />,    perm: 'raw-inventory' },
  { path: '/reader-manager',     label: 'Reader Manager',    icon: <MdRouter />,     perm: 'reader-manager' },
  { path: '/reports',            label: 'Reports',           icon: <MdBarChart />,   perm: 'reports' },
  { path: '/analytics',          label: 'Analytics',         icon: <MdInsights />,   perm: 'analytics',     adminOnly: true },
  { path: '/activity-logs',      label: 'Activity Logs',     icon: <MdHistory />,    perm: 'activity-logs',  adminOnly: true },
  { path: '/user-management',    label: 'User Management',   icon: <MdPeople />,     perm: 'user-management',adminOnly: true },
  // adminOnly, EXCEPT for a user granted 'template-fields-edit' — they get in
  // to maintain registration template fields (the page itself shows them only
  // that tab). `orPerm` satisfies both the admin gate and the perm gate below.
  { path: '/masters',            label: 'Masters',           icon: <MdViewList />,   perm: 'masters',        adminOnly: true, orPerm: 'template-fields-edit' },
  // Companies module: visible to Identium super-admins (manage everyone)
  // AND to reseller-tenant admins (manage their own children only). The
  // Companies page itself filters what each role sees & can do; the
  // sidebar entry just decides whether to show the link at all.
  { path: '/companies',          label: 'Companies',         icon: <MdBusiness />,   superAdminOrResellerOnly: true },
  // System Health: super-admin sees full infra dashboard; reseller sees
  // only their own children's DB stats. Backend filters per role.
  { path: '/system-health',      label: 'System Health',     icon: <MdMonitorHeart />, superAdminOrResellerOnly: true },
  { path: '/downloads',          label: 'Downloads',         icon: <MdSystemUpdate />, alwaysShow: true },
  { path: '/user-guide',         label: 'User Guide',        icon: <MdMenuBook />,   alwaysShow: true },
  { path: '/settings',           label: 'Settings',          icon: <MdSettings />,   perm: 'settings',       adminOnly: true },
];

const Sidebar = ({ collapsed, mobileOpen, isMobile, onClose }) => {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();

  // Per-tenant branding: fetch the active tenant's company_name + logo
  // from app_settings on mount. Each tenant's app_settings is in their own
  // DB (the legacy db proxy resolves via ALS-bound tenantId from JWT), so
  // Bata sees Bata's logo, Dabur sees Dabur's, Identium sees its own.
  const [branding, setBranding] = useState({ name: null, logoUrl: null });
  useEffect(() => {
    if (!user) return;
    api.get('/app-settings').then(r => {
      const s = r.data || {};
      setBranding({
        name: s.company_name || null,
        logoUrl: s.company_logo_path || null,
      });
    }).catch(() => {});
  }, [user]);

  // Build an authenticated logo URL (the /uploads middleware needs the JWT
  // either in the Authorization header — which <img> can't send — or as a
  // ?token= query param. We append it here so the <img> tag works.)
  const logoSrc = branding.logoUrl
    ? `${branding.logoUrl}?token=${encodeURIComponent(localStorage.getItem('identium_token') || '')}`
    : null;

  const handleLogout = async () => {
    // logout() handles its own navigation back to the per-company login
    // URL (preserves the slug from localStorage). Don't pre-empt with
    // navigate('/login') — that would land the user on the generic
    // Company-picker page, defeating the slug-aware redirect.
    await logout();
  };

  const sidebarStyle = {
    position: 'fixed',
    left: 0,
    top: 0,
    bottom: 0,
    width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)',
    background: 'linear-gradient(180deg, var(--primary-dark) 0%, var(--primary) 70%, var(--primary-light) 100%)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'width 0.25s ease, transform 0.25s ease',
    zIndex: 100,
    boxShadow: '3px 0 15px rgba(0,0,0,0.15)',
    // Mobile: slide in/out via transform
    ...(isMobile ? {
      transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
      width: 'var(--sidebar-width)',
    } : {}),
  };

  const logoStyle = {
    padding: collapsed ? '18px 0' : '18px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    justifyContent: collapsed ? 'center' : 'flex-start',
    flexShrink: 0,
  };

  return (
    <div style={sidebarStyle}>
      {/* Logo + mobile close. Per-tenant: shows the company's uploaded logo
          + name from app_settings. Falls back to a generic icon + their
          tenant name if no logo is uploaded yet, and finally to a static
          "ASSET / MANAGEMENT" placeholder if nothing is set at all. */}
      <div style={logoStyle}>
        <div style={{
          width: 38, height: 38, background: logoSrc ? 'white' : 'rgba(33,150,243,0.25)',
          borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, border: '1.5px solid rgba(33,150,243,0.4)', overflow: 'hidden',
        }}>
          {logoSrc ? (
            <img src={logoSrc} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                 onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <MdRssFeed style={{ color: '#64b5f6', fontSize: 22 }} />
          )}
        </div>
        {!collapsed && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'white', fontWeight: 700, fontSize: 13, lineHeight: 1.2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {(branding.name || user?.tenant?.name || 'ASSET MANAGEMENT').toUpperCase()}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, letterSpacing: '0.5px' }}>
              {user?.tenant?.slug ? user.tenant.slug.toUpperCase() : 'TAG MY ASSETS'}
            </div>
          </div>
        )}
        {isMobile && (
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)',
            cursor: 'pointer', fontSize: 22, padding: 4, marginLeft: 'auto',
          }}>
            <MdClose />
          </button>
        )}
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '10px 0' }}>
        {navItems.map(item => {
          // Super-admin gate: only the platform owner's admins (Identium
          // operators) see entries flagged superAdminOnly. Identified by
          // the tenant.is_platform_owner flag — independent of slug, so
          // renaming the tenant ("default" → "identium") doesn't break the
          // gate. Customer tenants never see these even if they're admins.
          const isSuperAdmin   = user?.role === 'admin' && !!user?.tenant?.is_platform_owner;
          const isResellerAdmin = user?.role === 'admin' && !!user?.tenant?.is_reseller;
          if (item.superAdminOnly && !isSuperAdmin) return null;
          if (item.superAdminOrResellerOnly && !(isSuperAdmin || isResellerAdmin)) return null;
          const viaOrPerm = !!item.orPerm && hasPermission(item.orPerm);
          if (item.adminOnly && user?.role !== 'admin' && !viaOrPerm) return null;
          // `alwaysShow` skips the per-module perm gate — used for help/info
          // pages (e.g. User Guide) that every signed-in user should see.
          if (!item.alwaysShow
              && !viaOrPerm
              && !item.superAdminOnly
              && !item.superAdminOrResellerOnly
              && !hasPermission(item.perm)) return null;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={isMobile ? onClose : undefined}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: collapsed ? '10px 0' : '10px 18px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                color: isActive ? 'white' : 'rgba(255,255,255,0.62)',
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                borderLeft: isActive ? '3px solid #2196F3' : '3px solid transparent',
                background: isActive ? 'rgba(33,150,243,0.14)' : 'transparent',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                position: 'relative',
              })}
              title={collapsed ? item.label : ''}
            >
              <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* User info + logout */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.08)',
        padding: collapsed ? '12px 0' : '12px 16px',
      }}>
        {!collapsed && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>{user?.full_name || user?.username}</div>
            <span style={{
              display: 'inline-block', marginTop: 3,
              background: user?.role === 'admin' ? 'rgba(33,150,243,0.3)' : 'rgba(255,255,255,0.15)',
              color: user?.role === 'admin' ? '#90caf9' : 'rgba(255,255,255,0.7)',
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
              textTransform: 'uppercase', letterSpacing: '0.5px'
            }}>
              {user?.role}
            </span>
          </div>
        )}
        <button
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(244,67,54,0.15)', color: '#ef9a9a',
            border: '1px solid rgba(244,67,54,0.2)', borderRadius: 8,
            padding: collapsed ? '8px 12px' : '8px 14px',
            cursor: 'pointer', fontSize: 13, fontWeight: 500,
            width: collapsed ? 'auto' : '100%',
            justifyContent: collapsed ? 'center' : 'flex-start',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244,67,54,0.28)'; e.currentTarget.style.color = '#ffcdd2'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244,67,54,0.15)'; e.currentTarget.style.color = '#ef9a9a'; }}
        >
          <MdLogout style={{ fontSize: 18, flexShrink: 0 }} />
          {!collapsed && 'Logout'}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
